'use client';

/**
 * Floating "help desk" support widget, rendered once at the shell root
 * by {@code SiteShellPresenter} when {@code shell.helpDesk.show} is
 * true. Ported from the legacy portal's
 * {@code components/Chat/ChatBox.js}: a floating button opens a panel
 * that either lists category rows or shows a quick ticket form
 * (subject / title / message), POSTed to the tenant's help center.
 *
 * <p>Distinct from the AI Chatbot ({@code ChatWidget}) — this reaches
 * a human via a support ticket, not a knowledge-base bot. Submits to
 * {@code /api/help-center/ticket}, a host route that proxies
 * {@code POST /v2/help-center/ticket} with the session's auth headers
 * attached server-side (see {@code apps/lms/portal/app/api/(lms)/help-center/ticket/route.ts}).
 *
 * <p>Built from {@code @akademi/ui}'s {@code Button}, {@code Input},
 * and {@code Textarea} primitives. Every piece of copy is either read
 * from {@code config} (localized, admin-editable) or falls back
 * through {@link BUILTIN} — never a bare literal.
 */
import { useEffect, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { ArrowLeft, Check, X } from 'lucide-react';
import type { HelpDeskCategory, HelpDeskWidgetConfig, LocalizedString } from '../../portal';
import { useLocale, t } from '../../portal';
import { BlockIcon } from '../icons/sidebarIcons';
import { Button, Input, Textarea } from '@akademi/ui';

export interface HelpDeskWidgetProps {
  config: HelpDeskWidgetConfig;
}

/** Built-in localized fallbacks — used whenever the tenant leaves a
 *  label field blank in the Shell editor, or for the small bits of
 *  chrome (back/close) that aren't worth their own schema field. */
const BUILTIN: Record<string, Record<string, string>> = {
  tooltip: { en: 'Support', tr: 'Destek' },
  panelTitle: { en: 'Support', tr: 'Destek' },
  menuHeading: { en: 'What do you need help with?', tr: 'Size nasıl yardımcı olabiliriz?' },
  formHeading: { en: 'Complete the form below', tr: 'Aşağıdaki formu doldurun' },
  subjectPlaceholder: { en: 'Subject', tr: 'Konu' },
  titlePlaceholder: { en: 'Title', tr: 'Başlık' },
  messagePlaceholder: { en: 'Write your message…', tr: 'Mesajınızı yazın…' },
  submitLabel: { en: 'Send', tr: 'Gönder' },
  successMessage: { en: 'Message sent successfully.', tr: 'Mesajınız başarıyla gönderildi.' },
  errorMessage: {
    en: 'Something went wrong while sending your message.',
    tr: 'Mesajınız gönderilirken bir hata oluştu.',
  },
  back: { en: 'Back', tr: 'Geri' },
  close: { en: 'Close', tr: 'Kapat' },
};

/** Default category rows — shown whenever the tenant hasn't authored
 *  its own list yet, mirroring the legacy ChatBox.js menu 1:1 so the
 *  widget is useful the moment it's switched on. */
const DEFAULT_CATEGORIES: HelpDeskCategory[] = [
  { icon: 'user', label: { en: 'Account', tr: 'Hesap' }, value: 'account' },
  { icon: 'play-circle', label: { en: 'Courses', tr: 'Kurslar' }, value: 'courses' },
  { icon: 'users', label: { en: 'Coordinators', tr: 'Koordinatörler' }, value: 'coordinators' },
  {
    icon: 'send',
    label: { en: 'Suggestion / request', tr: 'Öneri / talep' },
    value: 'suggestionRequest',
  },
  { icon: 'file-text', label: { en: 'FAQ', tr: 'SSS' }, value: 'faq' },
  { icon: 'shield', label: { en: 'Security', tr: 'Güvenlik' }, value: 'security' },
  { icon: 'help-circle', label: { en: 'Other', tr: 'Diğer' }, value: 'other' },
];

function resolve(
  value: LocalizedString | undefined,
  key: keyof typeof BUILTIN,
  locale: string,
): string {
  const resolved = t(value, locale);
  return resolved || BUILTIN[key]![locale] || BUILTIN[key]!['en']!;
}

/** Corner anchoring, mirroring {@code ScrollToTopButton}'s convention —
 *  clamp-based default, overridable via explicit px. */
function anchor(
  position: 'bottom-right' | 'bottom-left',
  offsetX?: number,
  offsetY?: number,
): CSSProperties {
  const x = offsetX != null ? `${offsetX}px` : 'clamp(16px, 3vw, 32px)';
  const y = offsetY != null ? `${offsetY}px` : 'clamp(16px, 3vw, 32px)';
  return position === 'bottom-left' ? { bottom: y, left: x } : { bottom: y, right: x };
}

export function HelpDeskWidget({ config }: HelpDeskWidgetProps) {
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [categoryValue, setCategoryValue] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Auto-dismiss the post-submit confirmation toast. Re-armed on every
  // new message so a second ticket sent while the first toast is still
  // fading out gets its own full display window.
  useEffect(() => {
    if (!toastMessage) return;
    const timer = window.setTimeout(() => setToastMessage(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toastMessage]);

  const categories =
    config.categories && config.categories.length > 0 ? config.categories : DEFAULT_CATEGORIES;
  const hasCategories = categories.length > 0;
  const showingForm = !hasCategories || categoryValue != null;
  const selectedCategory = categories.find((c) => c.value === categoryValue);
  const subjectEditable = !selectedCategory || selectedCategory.value === 'other';

  const reset = () => {
    setCategoryValue(null);
    setSubject('');
    setTitle('');
    setMessage('');
    setStatus('idle');
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const pickCategory = (value: string, label: string) => {
    setCategoryValue(value);
    setSubject(value === 'other' ? '' : label);
    setStatus('idle');
  };

  const back = () => {
    if (hasCategories) {
      setCategoryValue(null);
      setStatus('idle');
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !title.trim() || !message.trim() || status === 'submitting') return;
    setStatus('submitting');
    try {
      const res = await fetch('/api/help-center/ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: categoryValue ?? 'other',
          subject,
          message: `${title}\n\n${message}`,
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setToastMessage(resolve(config.successMessage, 'successMessage', locale));
      close();
    } catch {
      setStatus('error');
    }
  };

  const position = config.position ?? 'bottom-right';
  const cornerInset = anchor(position, config.offsetX, config.offsetY);
  const tooltipLabel = resolve(config.tooltip, 'tooltip', locale);
  const backLabel = resolve(undefined, 'back', locale);
  const closeLabel = resolve(undefined, 'close', locale);

  const buttonStyle: CSSProperties = {
    position: 'fixed',
    ...cornerInset,
    zIndex: 45,
    width: 44,
    height: 44,
    borderRadius: 999,
    display: 'grid',
    placeItems: 'center',
    border: 'none',
    cursor: 'pointer',
    background: 'var(--color-brand-500, #4f46e5)',
    color: 'var(--color-text-on-brand, #fff)',
    boxShadow: hovered
      ? '0 10px 28px -6px var(--color-brand-500, #4f46e5)'
      : 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.2))',
    padding: 0,
    overflow: 'hidden',
    transform: `scale(${hovered ? 1.06 : 1})`,
    transition: 'transform 220ms ease, box-shadow 220ms ease, background-color 200ms ease',
  };

  // Both the panel and the post-submit toast float just above the trigger
  // button, in its corner.
  const aboveButtonBottom =
    config.offsetY != null ? `${config.offsetY + 68}px` : 'calc(clamp(16px, 3vw, 32px) + 68px)';

  const panelStyle: CSSProperties = {
    position: 'fixed',
    ...cornerInset,
    bottom: aboveButtonBottom,
    zIndex: 46,
    width: 360,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 480,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'var(--radius-xl, 16px)',
    background: 'var(--color-surface-card, #fff)',
    color: 'var(--color-text-primary, #111)',
    boxShadow: 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.24))',
    border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
    overflow: 'hidden',
    transformOrigin: position === 'bottom-left' ? 'bottom left' : 'bottom right',
    opacity: open ? 1 : 0,
    transform: open ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.96)',
    pointerEvents: open ? 'auto' : 'none',
    transition: 'opacity 200ms ease, transform 200ms ease',
  };

  const toastVisible = toastMessage != null;
  const toastStyle: CSSProperties = {
    position: 'fixed',
    ...cornerInset,
    bottom: aboveButtonBottom,
    zIndex: 48,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    maxWidth: 320,
    padding: '12px 16px',
    borderRadius: 'var(--radius-lg, 10px)',
    background: 'var(--color-surface-card, #fff)',
    color: 'var(--color-text-primary, #111)',
    boxShadow: 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.24))',
    border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
    fontSize: 13,
    fontWeight: 500,
    opacity: toastVisible ? 1 : 0,
    transform: toastVisible ? 'translateY(0)' : 'translateY(8px)',
    pointerEvents: 'none',
    transition: 'opacity 200ms ease, transform 200ms ease',
  };

  return (
    <>
      <div
        style={panelStyle}
        role="dialog"
        aria-hidden={!open}
        aria-label={resolve(config.panelTitle, 'panelTitle', locale)}
        // Removes the whole subtree from the tab order + a11y tree while
        // closed, without unmounting it — the panel keeps its slide/fade
        // transition instead of popping in and out of the DOM.
        inert={!open}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '14px 16px',
            borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
          }}
        >
          {showingForm && hasCategories && (
            <Button variant="ghost" size="icon" onClick={back} aria-label={backLabel}>
              <ArrowLeft size={16} />
            </Button>
          )}
          <span style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>
            {resolve(config.panelTitle, 'panelTitle', locale)}
          </span>
          <Button variant="ghost" size="icon" onClick={close} aria-label={closeLabel}>
            <X size={16} />
          </Button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
          {!showingForm ? (
            <>
              <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 10px' }}>
                {resolve(config.menuHeading, 'menuHeading', locale)}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {categories.map((cat) => (
                  <CategoryRow
                    key={cat.value}
                    category={cat}
                    locale={locale}
                    onSelect={() => pickCategory(cat.value, t(cat.label, locale))}
                  />
                ))}
              </div>
            </>
          ) : (
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 2px' }}>
                {resolve(config.formHeading, 'formHeading', locale)}
              </p>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={resolve(config.subjectPlaceholder, 'subjectPlaceholder', locale)}
                disabled={!subjectEditable}
                required
              />
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={resolve(config.titlePlaceholder, 'titlePlaceholder', locale)}
                required
              />
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={resolve(config.messagePlaceholder, 'messagePlaceholder', locale)}
                required
                rows={4}
              />
              {status === 'error' && (
                <p
                  style={{
                    fontSize: 12,
                    margin: 0,
                    color: 'var(--color-status-error, #b91c1c)',
                  }}
                >
                  {resolve(config.errorMessage, 'errorMessage', locale)}
                </p>
              )}
              <Button type="submit" disabled={status === 'submitting'} style={{ marginTop: 2 }}>
                {resolve(config.submitLabel, 'submitLabel', locale)}
              </Button>
            </form>
          )}
        </div>
      </div>

      <div style={toastStyle} role="status" aria-live="polite" aria-hidden={!toastVisible}>
        <span
          style={{
            display: 'grid',
            placeItems: 'center',
            width: 20,
            height: 20,
            flexShrink: 0,
            borderRadius: '50%',
            background: 'var(--color-status-success, #16a34a)',
            color: '#fff',
          }}
        >
          <Check size={13} strokeWidth={3} />
        </span>
        {toastMessage}
      </div>

      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={tooltipLabel}
        aria-expanded={open}
        title={tooltipLabel}
        style={buttonStyle}
      >
        {config.image ? (
          <img
            src={config.image}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <BlockIcon value={config.icon || 'message-circle-question'} size={26} />
        )}
      </button>
    </>
  );
}

// =============================================================================
// CategoryRow — one row in the category picker
// =============================================================================

function CategoryRow({
  category,
  locale,
  onSelect,
}: {
  category: HelpDeskCategory;
  locale: string;
  onSelect: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        border: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        borderRadius: 'var(--radius-md, 6px)',
        padding: '9px 8px',
        fontSize: 14,
        fontWeight: 500,
        fontFamily: 'inherit',
        color: 'var(--color-text-primary, #111)',
        background: hovered ? 'var(--color-surface-muted, #f4f4f5)' : 'transparent',
        transition: 'background-color 0.12s ease',
      }}
    >
      <span
        style={{
          display: 'grid',
          placeItems: 'center',
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: 'var(--radius-md, 6px)',
          background: 'var(--color-surface-muted, #f1f5f9)',
          color: 'var(--color-brand-500, #4f46e5)',
        }}
      >
        <BlockIcon value={category.icon} size={16} />
      </span>
      {t(category.label, locale)}
    </button>
  );
}
